import { createSlice } from '@reduxjs/toolkit';

import * as sessionThunks from './sessionThunks';
import { SESSION_SLICE_NAME } from '../slicesNames';
import { isInvalidTokenRejection } from '../../actionMatchers';
import { getInitialAnonymousAnalyticsToken } from './sessionUtils';

export const getInitialState = () => ({
  memberExtra: null,
  isLoading: null,
  isError: null,
  isComplete: false,
  accessToken: null,
  anonymousAnalyticsToken: getInitialAnonymousAnalyticsToken(), // only used for analytics events when we don't have an access token to send
  ssoRedirectUrl: null,
});

const sessionSlice = createSlice({
  name: SESSION_SLICE_NAME,
  initialState: getInitialState(),
  reducers: {
    updateAccessToken(state, action) {
      const { accessToken } = action.payload;
      state.accessToken = accessToken;
      localStorage.setItem('integrationToken', state.accessToken);
    },
  },
  extraReducers(builder) {
    builder.addCase(sessionThunks.initializeSession.pending, (state) => {
      state.isLoading = true;
      state.isError = null;
      state.isComplete = false;
    });
    builder.addCase(sessionThunks.initializeSession.fulfilled, (state, action) => {
      const { memberExtra = {} } = action.payload;
      state.isLoading = false;
      state.memberExtra = memberExtra;
      state.isComplete = true;
      const { integrationToken = null } = action.meta.arg;

      if (integrationToken) {
        state.accessToken = integrationToken;
        localStorage.setItem('integrationToken', state.accessToken);
      }
    });
    builder.addCase(sessionThunks.initializeSession.rejected, (state, action) => {
      state.isLoading = false;
      state.accessToken = null;
      state.isComplete = true;
      if (action.error.status === 401) {
        state.isError = false; // 401 shouldn't be considered an error - it simply means the token was invalid. isError should be reserved for failures in the request or unexpected status codes.
      } else {
        state.isError = true;
      }
    });

    // listen for any and all rejections - specifically to clear tokens in the event of a 401
    builder.addMatcher(isInvalidTokenRejection, (state, action) => {
      const redirectUrl =
        action.error.data?.redirectUrl || // this is how an Axios error from within a thunk would return the redirect url
        action.error.data?.redirect_url || // this is just defensive programming for any call to Fusion that for some reason was NOT converted to camelCase - it should never happen
        action.payload?.data?.redirectUrl; // this is how an RTK Query fusionApi error would return the redirect url
      if (redirectUrl) state.ssoRedirectUrl = redirectUrl;

      state.accessToken = null;
      localStorage.removeItem('integrationToken');
    });
  },
});

export default sessionSlice;
export const { updateAccessToken } = sessionSlice.actions;
